import React, { useState, useEffect } from 'react';
import { useQC } from './QCContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { ClipboardList, Play, X, Clock, Users, MapPin, Calendar, ArrowRight, AlertTriangle, Filter, RefreshCw } from 'lucide-react';

export const QCStaffingRequests = () => {
  const { api, user } = useQC();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [turnSeconds, setTurnSeconds] = useState(300);

  useEffect(() => {
    fetchRequests();
  }, [filterStatus, filterPriority]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = '/quickcanvass/staffing-requests';
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      setRequests(response.data?.requests || []);
    } catch (error) {
      toast.error('Failed to fetch staffing requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCanvass = async () => {
    if (!selectedRequest) return;
    
    setStartLoading(true);
    try {
      const response = await api.post(`/canvass/crew-requests/${selectedRequest.id}/start`, {
        turn_seconds: turnSeconds
      });
      toast.success('Canvass started successfully!');
      setStartDialogOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start canvass');
    } finally {
      setStartLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!confirm('Are you sure you want to cancel this staffing request?')) return;
    
    try {
      await api.post(`/quickcanvass/staffing-requests/${requestId}/cancel`);
      toast.success('Request cancelled');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'emergency':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Emergency</Badge>;
      case 'urgent':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Urgent</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-800 border-slate-200">Normal</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'queued':
        return <Badge className="bg-blue-100 text-blue-800">Queued</Badge>;
      case 'in_progress':
        return <Badge className="bg-emerald-100 text-emerald-800">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-100 text-slate-600">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getModeBadge = (mode) => {
    return mode === 'DRAFT' 
      ? <Badge variant="outline" className="bg-indigo-50 text-indigo-700">Draft Mode</Badge>
      : <Badge variant="outline" className="bg-purple-50 text-purple-700">Open Bid</Badge>;
  };

  const formatCrewRequirements = (requirements) => {
    if (!requirements || Object.keys(requirements).length === 0) return 'Not specified';
    return Object.entries(requirements)
      .filter(([_, count]) => count > 0)
      .map(([role, count]) => `${role.replace('_', ' ')}: ${count}`)
      .join(', ');
  };

  const queuedCount = requests.filter(r => r.status === 'queued').length;
  const emergencyCount = requests.filter(r => r.priority === 'emergency' && r.status === 'queued').length;

  return (
    <div className="space-y-6" data-testid="qc-staffing-requests">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary" />
            Staffing Requests
          </h1>
          <p className="text-slate-600 mt-1">
            Incoming crew requests from Project Command Center
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRequests} data-testid="refresh-requests-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Queued Requests</p>
                <p className="text-3xl font-bold text-blue-600">{queuedCount}</p>
              </div>
              <Clock className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Emergency Priority</p>
                <p className="text-3xl font-bold text-red-600">{emergencyCount}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Requests</p>
                <p className="text-3xl font-bold text-slate-900">{requests.length}</p>
              </div>
              <Users className="h-10 w-10 text-slate-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <CardTitle className="text-base">Filters</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <label className="text-sm font-medium text-slate-600 mb-1 block">Status</label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-slate-600 mb-1 block">Priority</label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger data-testid="filter-priority">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Staffing Request Queue</CardTitle>
          <CardDescription>
            Review and start canvass sessions for incoming crew requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No staffing requests found</p>
              <p className="text-sm mt-1">Requests will appear here when created from the Command Center</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Work Order</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Crew Needed</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((req) => (
                  <TableRow 
                    key={req.id}
                    className={req.priority === 'emergency' ? 'bg-red-50/50' : req.priority === 'urgent' ? 'bg-amber-50/50' : ''}
                    data-testid={`staffing-request-${req.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{req.work_order_title}</p>
                        <p className="text-xs text-slate-500">ID: {req.work_order_id?.slice(0, 8)}...</p>
                      </div>
                    </TableCell>
                    <TableCell>{getPriorityBadge(req.priority)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-slate-400" />
                        <span className="text-sm">{req.crew_size_total || 0}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatCrewRequirements(req.crew_requirements)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span className="text-sm">{req.location || req.station_or_division || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span className="text-sm">
                          {new Date(req.requested_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">by {req.requested_by_name}</p>
                    </TableCell>
                    <TableCell>{getModeBadge(req.mode)}</TableCell>
                    <TableCell>{getStatusBadge(req.status)}</TableCell>
                    <TableCell className="text-right">
                      {req.status === 'queued' && (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(req);
                              setStartDialogOpen(true);
                            }}
                            data-testid={`start-canvass-${req.id}`}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Start
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelRequest(req.id)}
                            data-testid={`cancel-request-${req.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {req.status === 'in_progress' && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={`/canvass/draft-board?window=${req.canvass_window_id}`}>
                            <ArrowRight className="h-4 w-4 mr-1" />
                            View
                          </a>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Start Canvass Dialog */}
      <Dialog open={startDialogOpen} onOpenChange={setStartDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Canvass Session</DialogTitle>
            <DialogDescription>
              Configure and start the canvass for this staffing request
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedRequest.work_order_title}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {getPriorityBadge(selectedRequest.priority)}
                  {getModeBadge(selectedRequest.mode)}
                </div>
                <div className="mt-3 text-sm text-slate-600">
                  <p><strong>Crew Needed:</strong> {selectedRequest.crew_size_total || 0}</p>
                  <p><strong>Requirements:</strong> {formatCrewRequirements(selectedRequest.crew_requirements)}</p>
                  <p><strong>Location:</strong> {selectedRequest.location || 'N/A'}</p>
                </div>
              </div>

              {selectedRequest.mode === 'DRAFT' && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Turn Duration (seconds)
                  </label>
                  <Input
                    type="number"
                    value={turnSeconds}
                    onChange={(e) => setTurnSeconds(parseInt(e.target.value) || 300)}
                    min={60}
                    max={1800}
                    data-testid="turn-seconds-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    How long each person has to make their pick ({Math.floor(turnSeconds / 60)} min {turnSeconds % 60} sec)
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setStartDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStartCanvass} disabled={startLoading} data-testid="confirm-start-canvass">
              {startLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Start Canvass
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QCStaffingRequests;
